import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarCheck, ChevronLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../App';
import { ASSETS } from '../constants/site';

const fmt12 = (t) => {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const iso = (d) => d.toISOString().slice(0, 10);

export default function Book() {
  const days = useMemo(() => {
    const out = [];
    const d = new Date();
    while (out.length < 14) {
      if (d.getDay() !== 0 && d.getDay() !== 6) out.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return out;
  }, []);

  const [date, setDate] = useState(iso(days[0]));
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [time, setTime] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    setSlotsLoading(true);
    setTime('');
    api.get(`/availability?date=${date}`)
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!time) return;
    setBusy(true);
    try {
      const { data } = await api.post('/bookings', { ...form, date, time, ref: sessionStorage.getItem('gslab_ref') || '' });
      setBooked(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not book that slot. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050B14] circuit-bg pt-28 pb-20 px-4 sm:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 transition-colors mb-8">
            <ChevronLeft size={15} /> Back to GSLAB
          </Link>
          <div className="flex items-center gap-4 mb-3">
            <img src={ASSETS.icon} alt="GSLAB" className="h-12 w-12 rounded-xl" />
            <div>
              <h1 className="font-display font-bold text-2xl sm:text-3xl">Book a free consultation</h1>
              <p className="font-mono-tech text-[11px] tracking-[0.25em] text-amber-300/90 uppercase">30 min · Eastern Time · with Gabe</p>
            </div>
          </div>
        </motion.div>

        {booked ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="booking-confirmation" className="gold-ring rounded-3xl bg-[#0F1C33]/80 p-10 text-center mt-8">
            <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-5" />
            <h2 className="font-display font-bold text-2xl mb-2">You're booked in.</h2>
            <p className="text-slate-300">
              {new Date(booked.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              {' at '}<span className="text-amber-300 font-semibold">{fmt12(booked.time)} ET</span>
            </p>
            <p className="text-sm text-slate-400 mt-4">A confirmation email is on its way. Talk soon.</p>
            <Link to="/" className="inline-block mt-8 text-sm text-amber-300 hover:underline">Back to the site</Link>
          </motion.div>
        ) : (
          <div className="gold-ring rounded-3xl bg-[#0F1C33]/80 p-6 sm:p-8 mt-8">
            <div data-testid="booking-date-strip" className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1">
              {days.map((d) => {
                const dIso = iso(d);
                const active = dIso === date;
                return (
                  <button
                    key={dIso}
                    data-testid="booking-date-option"
                    onClick={() => setDate(dIso)}
                    className={`shrink-0 w-16 rounded-xl border py-3 text-center transition-colors duration-300 ${
                      active ? 'border-amber-400 bg-amber-500/10' : 'border-slate-700/60 hover:border-slate-500'
                    }`}
                  >
                    <div className={`font-mono-tech text-[10px] tracking-widest uppercase ${active ? 'text-amber-300' : 'text-slate-500'}`}>
                      {d.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`font-display font-bold text-lg ${active ? 'text-amber-300' : 'text-slate-200'}`}>{d.getDate()}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 min-h-[120px]">
              {slotsLoading ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center">
                  <Loader2 size={16} className="animate-spin" /> Checking Gabe's calendar…
                </div>
              ) : slots.length === 0 ? (
                <p className="text-slate-500 text-sm py-8 text-center">No open slots this day — pick another date.</p>
              ) : (
                <div data-testid="booking-slot-grid" className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slots.map((t) => (
                    <button
                      key={t}
                      data-testid="booking-slot-button"
                      onClick={() => setTime(t)}
                      className={`rounded-lg border py-2.5 text-sm font-mono-tech transition-colors duration-200 ${
                        time === t ? 'border-amber-400 bg-amber-500/10 text-amber-300' : 'border-slate-700/60 text-slate-300 hover:border-amber-500/40'
                      }`}
                    >
                      {fmt12(t)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {time && (
              <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="mt-6 space-y-3 border-t border-slate-800 pt-6">
                <p className="text-sm text-slate-300">
                  Booking <span className="text-amber-300 font-semibold">{fmt12(time)} ET</span> — almost done:
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="booking-name-input" required placeholder="Your name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-3 text-sm outline-none transition-colors placeholder:text-slate-600" />
                  <input data-testid="booking-email-input" required type="email" placeholder="Email address" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-3 text-sm outline-none transition-colors placeholder:text-slate-600" />
                </div>
                <input data-testid="booking-phone-input" type="tel" placeholder="Mobile number (optional — for SMS confirmation & reminder)" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-3 text-sm outline-none transition-colors placeholder:text-slate-600" />
                <textarea data-testid="booking-notes-input" rows={3} placeholder="What's your business, and what should the website do?" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-3 text-sm outline-none transition-colors placeholder:text-slate-600" />
                <button data-testid="booking-submit-button" type="submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-shadow duration-300 disabled:opacity-60">
                  {busy ? <Loader2 size={17} className="animate-spin" /> : <CalendarCheck size={17} />}
                  Confirm consultation
                </button>
              </motion.form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
